import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  BookOpen, Presentation, Trophy, ArrowRight, ArrowLeft, Sparkles,
  GraduationCap, School, Zap,
  FileText, Library, Users, ClipboardList, Gamepad2, Languages, Headphones,
  Bot, Search, Award, Wand2, HelpCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// 4-step first-time quickstart
const QUICK_STEPS = [
  { icon: Sparkles, title: "Welcome to Lectura!", body: "Turn any book chapter into a narrated lecture, slides, a printable study guide and a graded quiz — powered by Gemini 3.1 Pro." },
  { icon: BookOpen, title: "1. Name a book & chapter", body: "On Create, type a book title and the chapter(s) you're studying. Or paste the actual chapter text for the highest-fidelity results." },
  { icon: Presentation, title: "2. Get lecture + slides + guide", body: "In about half a minute you'll get a full lecture (readable and narrated), a slide deck, and a printable guide with end-of-chapter answers." },
  { icon: Trophy, title: "3. Test yourself or compete live", body: "Take the graded quiz solo, or launch a Live Class Game — students join with a room code and race for the podium." },
];

const FEATURES = [
  { id: "create-basic", audience: ["student", "instructor"], icon: Wand2,
    title: "Generate a lecture", tag: "Create",
    body: "Type a book + chapter → depth (Quick, Standard, Deep, Certification 100-Q · 45-slide) → get lecture, slides, key concepts, terms, citations and quiz. Add an Edition to lock the exact textbook version." },
  { id: "paste-text", audience: ["student", "instructor"], icon: FileText,
    title: "Paste-Text mode (highest fidelity)", tag: "Create · New",
    body: "On Create, choose the 3rd scope option 'Paste text' and drop in up to 80,000 characters of the actual chapter. Lectura writes every slide, quiz item and citation DIRECTLY from that source — perfect for niche or brand-new textbooks." },
  { id: "eoc-qa", audience: ["student", "instructor"], icon: HelpCircle,
    title: "End-of-Chapter Q&A answers", tag: "Guide · New",
    body: "When a chapter contains Concept Check / Recall / Critical Thinking / Review questions, Lectura transcribes them with professor-grade model answers. Reveal them one-by-one in the Guide, or during the slide presentation live in class." },
  { id: "languages", audience: ["student", "instructor"], icon: Languages,
    title: "27 languages + Bilingual mode", tag: "Create",
    body: "Every lecture, slide, quiz, tutor reply and Q&A is fluent in your chosen language. Bilingual mode shows two languages side-by-side — great for ESL / dual-immersion classrooms." },
  { id: "audio", audience: ["student", "instructor"], icon: Headphones,
    title: "AI voice narration", tag: "Lecture",
    body: "Every lecture can be narrated aloud with adjustable speed (0.75× – 1.5×). Great for commutes, chores, or auditory learners." },
  { id: "tutor", audience: ["student", "instructor"], icon: Bot,
    title: "Ask the Professor", tag: "Every tab",
    body: "Any lecture, term or wrong quiz answer → hit 'Ask the Professor' for a targeted AI explanation. Use 'Explain this deeper' after a wrong answer for a richer walkthrough." },
  { id: "focus", audience: ["student"], icon: Search,
    title: "Focus Mode (weakness targeting)", tag: "Quiz",
    body: "After any quiz, Focus Mode surfaces the concepts you missed and re-quizzes ONLY those until your mastery hits green. Dashboard tracks progress over time." },
  { id: "share", audience: ["student"], icon: Award,
    title: "Share your grade", tag: "Quiz",
    body: "After a quiz, tap 'Share your grade' — Lectura generates a beautiful IG-story card with your score to post or DM." },
  { id: "live-game", audience: ["instructor", "student"], icon: Gamepad2,
    title: "Live Class Game Mode", tag: "New · Better than Kahoot",
    body: "Instructor picks any lecture with ≥3 quiz Qs → clicks Live in the Class Library or My Library. Big projector view shows a 6-char room code. Students join at /join-game on their phones. 4 colored buttons, 20-sec timer, live leaderboard, animated podium at the end. Powered by YOUR chapter's questions, not made-up trivia." },
  { id: "power-ups", audience: ["instructor", "student"], icon: Zap,
    title: "Power-Ups (Combo · Shield · Steal)", tag: "Live Game · New",
    body: "Only in Live Class Game Mode. 3-in-a-row = Combo Boost (2× on next correct). 5-in-a-row = Shield (auto-save streak on one wrong answer). 10-in-a-row = Steal (take 200 pts from the leader on your next correct)." },
  { id: "class-library", audience: ["instructor"], icon: Library,
    title: "Class Libraries (staging area)", tag: "Instructor · New",
    body: "Every class you teach has a private staging area, hidden from students until you post. On Create, pick 'Save to a class library' and set a Chapter label. Lectures get filed by chapter inside the class. Students only see what you explicitly assign or run as a live game." },
  { id: "classes", audience: ["instructor"], icon: School,
    title: "Classes, join codes & assignments", tag: "Instructor",
    body: "Create a class → get a 6-char code / shareable join link. Students join, appear on your roster. Post assignments (individual generation or shared lecture). See per-student score/time heatmap and export a full CSV gradebook." },
  { id: "gradebook", audience: ["instructor"], icon: ClipboardList,
    title: "CSV gradebook + heatmap", tag: "Instructor",
    body: "Every classroom has a CSV export button and a color-coded heatmap of student × assignment scores. Works with Google Sheets, Excel, or any LMS import." },
  { id: "referral", audience: ["student", "instructor"], icon: Users,
    title: "Referral rewards", tag: "Profile",
    body: "Every user has a personal referral link. Refer a friend → they get 7 free lectures (up from 3), you get +30 days of Pro. Leaderboard for top referrers." },
];

const TABS = [
  { id: "student", label: "For Students", icon: GraduationCap },
  { id: "instructor", label: "For Instructors", icon: School },
];

export const OnboardingTour = ({ open, onClose }) => {
  const { user } = useAuth();
  const isInstructor = user && (user.role === "instructor" || user.role === "admin");

  const [mode, setMode] = useState("quickstart");
  const [step, setStep] = useState(0);
  const [role, setRole] = useState(isInstructor ? "instructor" : "student");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    const seen = localStorage.getItem("lectura_tour_seen");
    setMode(seen ? "menu" : "quickstart");
    setStep(0);
    setRole(isInstructor ? "instructor" : "student");
    setQuery("");
  }, [open, isInstructor]);

  const finishQuick = () => {
    localStorage.setItem("lectura_tour_seen", "1");
    setMode("menu");
    setStep(0);
  };

  const s = QUICK_STEPS[step];
  const Icon = s?.icon;

  const items = FEATURES
    .filter((f) => f.audience.includes(role))
    .filter((f) => !query || (f.title + " " + f.body + " " + f.tag).toLowerCase().includes(query.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl overflow-hidden p-0">
        <DialogTitle className="sr-only">How to Use Lectura</DialogTitle>
        <DialogDescription className="sr-only">Role-aware feature guide.</DialogDescription>

        {mode === "quickstart" ? (
          <div className="p-6 sm:p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.22 }}
                className="text-center pt-2"
              >
                <div className="grid place-items-center h-16 w-16 mx-auto rounded-2xl bg-accent/15 text-accent">
                  <Icon className="h-8 w-8" />
                </div>
                <h2 className="mt-5 font-serif text-2xl font-bold">{s.title}</h2>
                <p className="mt-2 text-muted-foreground leading-relaxed max-w-md mx-auto">{s.body}</p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex items-center justify-center gap-1.5">
              {QUICK_STEPS.map((_, i) => (
                <span key={i} className={`h-2 rounded-full transition-all ${i === step ? "w-6 bg-accent" : "w-2 bg-border"}`} />
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <button data-testid="tour-skip" onClick={finishQuick}
                      className="text-sm font-semibold text-muted-foreground hover:text-foreground">
                Skip to menu
              </button>
              <div className="flex gap-2">
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)}
                          className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary/50 transition">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                )}
                <button
                  data-testid="tour-next"
                  onClick={() => (step + 1 >= QUICK_STEPS.length ? finishQuick() : setStep(step + 1))}
                  className="flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold hover:opacity-90 transition">
                  {step + 1 >= QUICK_STEPS.length ? "Explore the menu" : "Next"} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col max-h-[85vh]">
            <div className="p-5 sm:p-6 border-b border-border">
              <div className="flex items-center gap-2 text-accent">
                <Sparkles className="h-5 w-5" />
                <h2 className="font-serif text-2xl font-bold">How to Use Lectura</h2>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">The complete feature guide. Pick your role.</p>

              <div className="mt-4 flex rounded-full border border-border p-0.5 w-fit">
                {TABS.map((t) => {
                  const TIcon = t.icon;
                  const active = role === t.id;
                  return (
                    <button
                      key={t.id}
                      data-testid={`usage-tab-${t.id}`}
                      onClick={() => setRole(t.id)}
                      className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    >
                      <TIcon className="h-4 w-4" /> {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  data-testid="usage-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search features (live game, gradebook, focus mode…)"
                  className="w-full rounded-full border border-input bg-background pl-9 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition"
                />
              </div>
            </div>

            <div className="p-5 sm:p-6 overflow-y-auto flex-1">
              {items.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No features match "{query}".</p>
              ) : (
                <ul className="space-y-3">
                  {items.map((f) => {
                    const FIcon = f.icon;
                    return (
                      <li key={f.id} data-testid={`usage-item-${f.id}`}
                          className="rounded-2xl border border-border bg-card p-4 flex gap-4 hover:border-accent/40 transition">
                        <div className="grid place-items-center h-10 w-10 rounded-xl bg-accent/10 text-accent shrink-0">
                          <FIcon className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold">{f.title}</h3>
                            {f.tag && <span className="rounded-full bg-secondary/70 text-muted-foreground px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">{f.tag}</span>}
                          </div>
                          <p className="mt-1 text-sm text-foreground/80 leading-relaxed">{f.body}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="p-4 border-t border-border flex items-center justify-between gap-3 bg-secondary/30">
              <button
                data-testid="usage-replay-tour"
                onClick={() => { setMode("quickstart"); setStep(0); }}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              >
                <Sparkles className="h-3.5 w-3.5" /> Replay quick tour
              </button>
              <button
                data-testid="usage-close"
                onClick={onClose}
                className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold hover:opacity-90 transition"
              >
                Got it
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
